import React, { useContext, useState, useMemo, useEffect, useRef } from 'react';
import styles from './styles/ClassroomCanvasOptions.module.css';
import CanvasContext from '../../../context/CanvasContext';
import { FaChevronRight, FaChevronDown, FaHistory, FaCheckSquare, FaKeyboard, FaMapMarkerAlt } from 'react-icons/fa';

const ClassroomCanvasOptions = ({ userInfo }) => {
    const {
        classroomStudentsWithLogs,
        assignmentWithSubmissions,
        highlightStudentLog,
        highlightSubmission,
        resetHighlightSubmission,
    } = useContext(CanvasContext);

    const allowMultiExpand = false;
    const [expandedItems, setExpandedItems] = useState(allowMultiExpand ? new Set() : null);
    const [currentSelectedStudent, setSelectedStudent] = useState(userInfo?.userId);

    const handleSubmissionClick = (submission) => {
        highlightSubmission(submission);

        if (allowMultiExpand) {
            setExpandedItems(prevIds => {
                const newIds = new Set(prevIds);
                if (newIds.has(submission._id)) {
                    newIds.delete(submission._id);
                } else {
                    newIds.add(submission._id);
                }
                return newIds;
            });
        } else {
            setExpandedItems(prevId =>
                prevId === submission._id ? null : submission._id
            );
        }
    };

    const isScenarioMode = false;

    const dataSource = useMemo(() => {
        if (isScenarioMode) return classroomStudentsWithLogs;

        const userSubmissions = assignmentWithSubmissions?.submissions?.filter(
            sub => sub.viewer && sub.viewer._id === userInfo?.userId
        );

        if (!userSubmissions?.length) return [];

        return userSubmissions.sort(
            (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        );
    }, [assignmentWithSubmissions?.submissions, userInfo?.userId, isScenarioMode, classroomStudentsWithLogs]);

    const prevUserSubmissionsRef = useRef(null);

    useEffect(() => {
        if (!assignmentWithSubmissions?.submissions || !userInfo?.userId) return;
        
        if (userInfo?.userId !== currentSelectedStudent){
            resetHighlightSubmission();
            setSelectedStudent(userInfo?.userId);
        }

        const userSubmissions = assignmentWithSubmissions.submissions.filter((sub) => sub.viewer && sub.viewer._id === userInfo.userId);
        const latestSubmissions = userSubmissions.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

        const serialized = JSON.stringify(latestSubmissions);

        if (prevUserSubmissionsRef.current !== serialized) {
            prevUserSubmissionsRef.current = serialized;
            if (latestSubmissions.length === 0) {
                resetHighlightSubmission();
            }
        }
    }, [assignmentWithSubmissions?.submissions, userInfo?.userId]);
    
    useEffect(() => {
        setExpandedItems(allowMultiExpand ? new Set() : null);
    }, [userInfo?.userId]);

    const getDecisionClass = (decision) => {
        const classes = [styles.decisionItem];
        if (decision.type === 'choice' || decision.type === 'input') {
            if (decision.correctness === 'correct') {
                classes.push(styles.correct);
            } else if (decision.correctness === 'incorrect') {
                classes.push(styles.incorrect);
            }
        }
        return classes.join(' ');
    };


    if (!dataSource?.length) {
        return (
            <div className={styles.container}>
                <p>No {isScenarioMode ? 'student logs' : 'submissions'} available.</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2>{isScenarioMode ? 'Classroom Logs' : 'Assignment Submissions'}</h2>
            </div>
            <div className={styles.optionsContainer}>
                {dataSource.map((submission) => {
                    const isExpanded = allowMultiExpand
                        ? expandedItems.has(submission._id)
                        : expandedItems === submission._id;

                    const gradableDecisions = submission.decisions.filter(
                        d => (d.type === 'choice' || d.type === 'input') && d.correctness
                    );
                    
                    const correctCount = gradableDecisions.filter(d => d.correctness === 'correct').length;
                    const totalGradable = gradableDecisions.length;
                    
                    let correctnessPercentage = null;
                    if (totalGradable > 0) {
                        correctnessPercentage = Math.round((correctCount / totalGradable) * 100);
                    }

                    return (
                        <div key={submission._id} className={styles.accordionItem}>
                            <button
                                className={styles.accordionHeader}
                                onClick={() => handleSubmissionClick(submission)}
                            >
                                <div className={styles.headerInfo}>
                                    <span className={styles.colorDot} style={{ backgroundColor: submission.color }} />
                                    <div className={styles.headerText}>
                                        <h3>{submission.viewer.username}</h3>
                                        <div className={styles.headerMetadata}>
                                            <span>{new Date(submission.updatedAt).toLocaleString()}</span>
                                            {correctnessPercentage !== null && (
                                                <span className={styles.percentageDisplay}>
                                                    {`${correctnessPercentage}% Correct`}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                            </button>
                            {isExpanded && (
                                <div className={styles.accordionContent}>
                                    <ul className={styles.decisionList}>
                                        {submission.decisions.length > 0 ? (
                                            submission.decisions
                                                .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                                                .map((decision, index) => (
                                                    <li key={`${decision._id}-${index}`} className={getDecisionClass(decision)}>
                                                        {decision.type === 'situation' && (
                                                            <>
                                                                <FaMapMarkerAlt className={styles.decisionIcon} />
                                                                <span>Situation: {decision.title || 'untitled'}</span>
                                                            </>
                                                        )}
                                                        {decision.type === 'choice' && (
                                                            <>
                                                                <FaCheckSquare className={styles.decisionIcon} />
                                                                <span>Choice: {decision.title || 'untitled'}</span>
                                                            </>
                                                        )}
                                                        {decision.type === 'input' && (
                                                            <>
                                                                <FaKeyboard className={styles.decisionIcon} />
                                                                <span>Input: "{decision.userInput}"</span>
                                                            </>
                                                        )}
                                                    </li>
                                                ))
                                        ) : (
                                            <li className={styles.emptyStateText}>No decisions recorded.</li>
                                        )}
                                    </ul>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ClassroomCanvasOptions;